import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/free-solid-svg-icons';

import { TertiaryButton } from '@/components/button/Button.jsx';
import { TONE_ICONS } from '@/components/status-pill/statusTones.js';

import './Alert.css';

const cx = (...parts) => parts.filter(Boolean).join(' ');

const POSITIONS = new Set([
    'top-left', 'top-center', 'top-right',
    'bottom-left', 'bottom-center', 'bottom-right',
]);

const hosts = new Map();

const hostFor = (position) => {
    let host = hosts.get(position);
    if (!host) {
        const [vertical, horizontal] = position.split('-');
        host = document.createElement('div');
        host.className = `alert-container alert-container--${vertical} alert-container--${horizontal}`;
        document.body.appendChild(host);
        hosts.set(position, host);
    }
    return host;
};

const Alert = ({
    children,
    status = 'info',
    title,
    icon = true,
    dismissible = false,
    duration = 2000,
    onDismiss,
    position = 'bottom-right',
}) => {
    const [visible, setVisible] = useState(true);
    const onDismissRef = useRef(onDismiss);
    onDismissRef.current = onDismiss;

    const dismiss = useCallback(() => {
        setVisible(false);
        onDismissRef.current?.();
    }, []);

    // Deps must not include onDismiss: an inline one restarts the timer every render.
    useEffect(() => {
        if (!duration) return undefined;
        const timer = setTimeout(dismiss, duration);
        return () => clearTimeout(timer);
    }, [duration, dismiss]);

    if (!visible) return null;

    const glyph = icon === true ? TONE_ICONS[status] : icon;

    return createPortal(
        <div
            className={cx('alert', status !== 'info' && `alert--${status}`)}
            role={status === 'danger' || status === 'warning' ? 'alert' : 'status'}
        >
            {glyph && <FontAwesomeIcon icon={glyph} className="alert-icon" />}
            <div className="alert-text">
                {title && <div className="alert-title">{title}</div>}
                {children && <div className="alert-message">{children}</div>}
            </div>
            {dismissible && (
                <TertiaryButton
                    status={status}
                    size="small"
                    icon={faXmark}
                    aria-label="Dismiss"
                    onClick={dismiss}
                />
            )}
        </div>,
        hostFor(POSITIONS.has(position) ? position : 'bottom-right'),
    );
};

export default Alert;
